import { lighten, useTheme } from '@material-ui/core';
import { sumBy } from 'lodash';
import * as React from 'react';
import { useMemo } from 'react';
import { BarChart } from '../../../../components/muiLegacy/Charts/BarChart';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';

export interface TopTagsProps extends ChartProps {}

const TopTags: React.FC<TopTagsProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  React.useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showSerpOnly: true,
          showLimitItems: true,
          showTagGroupId: true,
        },
      })),
    []
  );

  // Fetch data
  const topTagsQuery = projectsApiService.queries.analysis.useGetTopTags({
    projectId: project.id,
    serpFilter: analysisFilterState?.serpOnly,
    limitItems: analysisFilterState?.limitItems,
    tagGroupId: analysisFilterState?.tagGroupId,
  });

  const topTagsLoading = topTagsQuery?.query?.loading;
  const topTagsResult = topTagsQuery?.result;

  const chartData = useMemo(() => {
    const data = topTagsResult?.topTags ? [...topTagsResult?.topTags] : [];
    if (topTagsResult?.otherTags?.length > 0) {
      const other = {
        tagGroupId: 0,
        tagId: -1,
        tagName: 'other (' + topTagsResult?.otherTags?.length + ')',
        searchVolume: sumBy(topTagsResult?.otherTags ?? [], (c) => c.searchVolume),
      };
      data.splice(data.length, 0, other);
    }
    return data.reverse();
  }, [topTagsResult]);

  // Styling
  const theme = useTheme();
  const topPadding = theme.spacing(8);
  const bottomPadding = theme.spacing(4);
  const rightPadding = theme.spacing(18);
  const leftPadding = theme.spacing(36);

  // Render
  if (didQueryTimeout(topTagsQuery?.query)) {
    return <DataTimedOut />;
  }

  if (topTagsLoading) {
    return <AnalysisChartLoading />;
  }

  if (!chartData.length) {
    return <NotEnoughData />;
  }

  return (
    <BarChart
      height={2 * topPadding + chartData.length * 40}
      palette={palette}
      ResponsiveBarProps={{
        data: chartData,
        layout: 'horizontal',
        enableGridY: false,
        enableGridX: true,
        keys: ['searchVolume'],
        indexBy: 'tagId',
        colors: (v: any) => {
          return v.indexValue === -1 ? palette.tertiaryScale[0] : palette.tertiaryScale[1];
        },
        borderRadius: 4,
        borderWidth: 0,
        theme: {
          fontSize: 14,
          textColor: '#000',
          axis: {
            ticks: {
              text: {
                fontSize: 14,
                fontWeight: theme.typography.fontWeightMedium,
              },
            },
          },
        },
        margin: {
          top: topPadding,
          bottom: bottomPadding,
          right: rightPadding,
          left: leftPadding,
        },
        axisLeft: {
          format: (v: any) => chartData.find((x) => x.tagId === v)?.tagName,
          tickSize: 0,
          tickPadding: 16,
          tickRotation: 0,
        },
        axisTop: {
          format: '~s',
          tickSize: 0,
          tickPadding: 5,
          tickRotation: 0,
          legend: internationalText.chart.keyword_cluster_by_demand.legend[language],
          legendPosition: 'middle',
          legendOffset: -50,
        },
        axisBottom: null,
      }}
    />
  );
};

export default TopTags;
